import React, {useEffect} from 'react';
import {Translation, withTranslation, WithTranslation} from 'react-i18next';
import {useNavigate} from "react-router-dom";
import 'draft-js/dist/Draft.css';
import {AuthState} from "./model/AuthState";
import {OAuth2TokenResponse, useAuth} from "./service/AuthProvider";
import {getAuthClientId, getAuthUrl} from "./api/Api";
import {useQuery} from "@tanstack/react-query";


const AuthCallback = (props: AuthCallbackProps) => {
    const {user, login, logout, getAccessToken} = useAuth();

    const navigate = useNavigate();

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code') ?? undefined;
    const state = params.get('state') ?? undefined;

    let authState: AuthState | undefined;
    try {
        authState = state ? JSON.parse(state) : undefined;
    } catch (e) {
        // ignore malformed state
        authState = undefined;
    }

    const redirectUrl = authState?.redirectAfterAuthUrl ?? "/";
    const isLoginAttempt: boolean = authState?.loggingIn === true;
    const isLogoutAttempt: boolean = !isLoginAttempt;

    const {data, isError, isFetched, isSuccess} = useQuery({
        queryKey: ['authToken', code, redirectUrl, isLoginAttempt],
        queryFn: async () => {
            if (!code) throw new Error('Authorization code not found in the callback URL.');

            const body = new URLSearchParams({
                grant_type: 'authorization_code',
                code: code,
                redirect_uri: `${window.location.origin}/auth/callback`,
                client_id: getAuthClientId()
            });

            const res = await fetch(`${getAuthUrl()}/oauth2/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: body
            })
            if (!res.ok) throw new Error('Failed to fetch auth/callback');
            const data = await (res.json() as Promise<OAuth2TokenResponse>)
            login(data)
            return data
        },

        enabled: isLoginAttempt && !!code,
    });

    useEffect(() => {
        if (isLogoutAttempt) {
            logout()
            navigate(redirectUrl)
        }
    }, [isLogoutAttempt, redirectUrl, logout, navigate]);

    useEffect(() => {
        if (isLoginAttempt && !!data && isSuccess && isFetched) {
            navigate(redirectUrl)
        }
    }, [isLoginAttempt, data, login, redirectUrl, navigate, isSuccess, isFetched]);

    if (isError) {
        return (
            <Translation>
                {t =>
                    <div>
                        <h2>Login failed</h2>
                        <p>Redirect to <a href={redirectUrl}>{window.location.origin}{redirectUrl}</a> canceled</p>
                    </div>
                }
            </Translation>
        )
    }

    return (
        <Translation>
            {t =>
                <div>
                    <h2>Redirecting...</h2>
                    <p>Redirecting to <a href={redirectUrl}>{window.location.origin}{redirectUrl}</a>...</p>
                </div>
            }
        </Translation>
    )
}

interface AuthCallbackProps extends WithTranslation {

}

export default withTranslation()(AuthCallback)

